import { functionWithFG } from '@atlaskit/platform-feature-flags-react/function-with-fg';

import { type Product } from '../methods/useProductFetchClient';
import { graphql, fetchQuery, type Environment } from 'react-relay';
import { createAtlAttributionHeader } from '../../../utils';
import { type getTenantRegionQuery } from './__generated__/getTenantRegionQuery.graphql';

let tenantRegionResult: Promise<string | null> | undefined;

// Exported for testing purposes only
export const resetTenantRegionResult = (): void => {
	tenantRegionResult = undefined;
};

const getTenantRegionRelay = ({
	product,
	cloudId,
	relayEnvironment,
}: Args): Promise<string | null> => {
	if (!!tenantRegionResult) {
		return tenantRegionResult;
	}

	if (!relayEnvironment || !cloudId) {
		return Promise.resolve(null);
	}

	try {
		const observable = fetchQuery<getTenantRegionQuery>(
			relayEnvironment,
			graphql`
				query getTenantRegionQuery($cloudIds: [ID!], $product: String!) {
					tenantContexts(cloudIds: $cloudIds) {
						activationIdByProduct(product: $product) {
							targetRegion
						}
					}
				}
			`,
			{
				cloudIds: [cloudId],
				product,
			},
			{
				networkCacheConfig: {
					metadata: {
						headers: {
							'atl-attribution': createAtlAttributionHeader(),
						},
					},
				},
			},
		);

		tenantRegionResult = new Promise<string | null>((resolve) => {
			observable.subscribe({
				next: (data) => {
					resolve(data?.tenantContexts?.[0]?.activationIdByProduct?.targetRegion ?? null);
				},
				error: () => {
					resolve(null);
				},
			});
		});

		return tenantRegionResult;
	} catch {
		return Promise.resolve(null);
	}
};

type Args = {
	cloudId?: string;
	product: Product;
	relayEnvironment?: Environment;
};

export const getTenantRegion: ({
	product,
	cloudId,
	relayEnvironment,
}: Args) => Promise<string | null> = functionWithFG(
	'forge-ui-enable-region-steering',
	getTenantRegionRelay,
	() => Promise.resolve(null),
);
